import React, { useRef, useEffect } from 'react'
import type { XYCoord } from 'react-dnd'
import { useDragLayer } from 'react-dnd'
import type { StyleProp } from '@vtex/admin-ui'
import { csx } from '@vtex/admin-ui'
import { identity } from 'ramda'

import type { DragData } from '../../shared/types'
import { List } from '../../shared/types'
import { OutOfStoreListItem } from '../OutOfStoreList/OutOfStoreListItem'
import { InStoreListItem } from '../InStoreList/InStoreListItem'
import type { ListsItem } from '../../context/TurnListProvider'

const containerStyles: StyleProp = {
  position: 'fixed',
  inset: 0,
  pointerEvents: 'none',
  zIndex: 5,
  height: '100%',
}

type DragLayer = {
  item: DragData<
    ListsItem['waiting'] | ListsItem['inService'] | ListsItem['outOfStore']
  >
  isDragging: boolean
}

type DragMoveCallback = (args: {
  initialOffset: XYCoord | null
  currentOffset: XYCoord | null
}) => void

export const CustomDragLayer = () => {
  const draggingElementRef = useRef<HTMLDivElement>(null)

  const onDragMove: DragMoveCallback = ({ initialOffset, currentOffset }) => {
    if (!draggingElementRef.current || !initialOffset || !currentOffset) {
      return
    }

    const { x, y } = currentOffset

    draggingElementRef.current.style.transform = `translate3d(${x}px, ${y}px, 0)`
  }

  const { isDragging, item } = useDragLayer<DragLayer>((monitor) => {
    onDragMove({
      initialOffset: monitor.getInitialSourceClientOffset(),
      currentOffset: monitor.getSourceClientOffset(),
    })

    return {
      item: monitor.getItem(),
      isDragging: monitor.isDragging(),
    }
  })

  useEffect(() => {
    if (draggingElementRef.current) {
      draggingElementRef.current.style.width = `${item.element.offsetWidth}px`
    }
  }, [item?.element.offsetWidth])

  const renderItem = () => {
    const testId = 'turn-list-item-drag-preview'

    switch (item.queueItem.kind) {
      case List.OutOfStore:
        return (
          <OutOfStoreListItem
            index={0}
            outOfStoreItem={item.queueItem}
            usedAsDragPreview
            data-testid={testId}
          />
        )

      case List.Waiting:
        return (
          <InStoreListItem
            index={0}
            item={item.queueItem}
            dndType={List.Waiting}
            onClick={identity}
            usedAsDragPreview
            testId={testId}
          />
        )

      case List.InService:
        return (
          <InStoreListItem
            index={0}
            item={item.queueItem}
            dndType={List.InService}
            onClick={identity}
            usedAsDragPreview
            testId={testId}
          />
        )

      default:
        return null
    }
  }

  if (!isDragging) {
    return null
  }

  return (
    <div className={csx({ ...containerStyles })}>
      <div ref={draggingElementRef}>{renderItem()}</div>
    </div>
  )
}
